import { Check, Sparkles, Crown, ArrowRight, Car } from "lucide-react";
import { useVehicle, CATEGORIES } from "@/contexts/VehicleContext";
import { usePricing } from "@/contexts/PricingContext";


const SERVICES = [
  {
    id: "exterieur",
    name: "Extérieur simple",
    tagline: "La brillance essentielle",
    features: [
      "Carrosserie lavée",
      "Bas de caisse",
      "Jantes",
      "Contours de portes",
      "Vitres extérieures",
      "Séchage du véhicule",
    ],
  },
  {
    id: "interieur",
    name: "Intérieur simple",
    tagline: "Habitacle impeccable",
    features: [
      "Aspiration complète de l'habitacle",
      "Nettoyage tableau de bord",
      "Plastiques et contours",
      "Garniture de portes",
      "Vitres intérieures",
    ],
  },
  {
    id: "pack",
    name: "Pack complet",
    tagline: "Intérieur + Extérieur",
    highlight: true,
    features: [
      "Tout l'Intérieur simple",
      "Tout l'Extérieur simple",
      "Finitions soignées",
      "Résultat uniforme premium",
      "Combinaison parfaite",
    ],
  },
  {
    id: "concession",
    name: "Formule Concession",
    tagline: "Expérience Premium",
    premium: true,
    features: [
      "Tout le Pack Complet",
      "Shampouineuse sièges & moquettes",
      "Rénovation plastiques int/ext",
      "Désinfection volant & commandes",
      "Traitement plastique/cuir",
      "Traitement hydrophobe",
    ],
  },
];

export default function Services() {
  const { category, categoryData, openSelector } = useVehicle();

  return (
    <section id="services" className="relative py-28 lg:py-36 border-t border-white/5" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row justify-between lg:items-end mb-10 gap-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">
              / 02 — Formules de nettoyage
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-6">
              Des formules pensées<br />pour <span className="text-gradient-red">chaque exigence.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-sm leading-relaxed">
            Prestations transparentes, tarifs clairs. Toutes nos formules sont réalisées à domicile, en toute autonomie.
          </p>
        </div>

        {/* Vehicle banner */}
        <VehicleBanner category={category} categoryData={categoryData} openSelector={openSelector} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s) => (
            <ServiceCard
              key={s.id}
              service={s}
              category={category}
              categoryData={categoryData}
              openSelector={openSelector}
            />
          ))}
        </div>

        <div className="mt-10 text-center text-white/40 text-sm">
          {category
            ? `* Tarifs affichés pour la ${categoryData.label} · ${categoryData.name}. Variable selon l'état du véhicule.`
            : "* Sélectionnez votre catégorie de véhicule pour afficher les tarifs exacts."}
        </div>
      </div>
    </section>
  );
}

function VehicleBanner({ category, categoryData, openSelector }) {
  if (!category) {
    return (
      <div className="mb-10 p-6 sm:p-8 rounded-xl border border-[#E50914]/40 bg-gradient-to-r from-[#E50914]/10 via-[#E50914]/5 to-transparent flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="vehicle-banner-empty">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#E50914]/20 border border-[#E50914]/40 flex items-center justify-center shrink-0">
            <Car className="w-6 h-6 text-[#E50914]" />
          </div>
          <div>
            <div className="font-bold text-white text-lg">Votre véhicule n'est pas encore sélectionné</div>
            <div className="text-sm text-white/60">Choisissez votre catégorie pour voir les tarifs exacts adaptés.</div>
          </div>
        </div>
        <button
          onClick={openSelector}
          data-testid="services-select-vehicle-btn"
          className="inline-flex items-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white font-bold uppercase text-sm tracking-wider px-6 py-3 rounded-lg border border-[#E50914] hover:shadow-[0_0_24px_rgba(229,9,20,0.4)] transition-all whitespace-nowrap"
        >
          Choisir mon véhicule <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="mb-10 p-5 sm:p-6 rounded-xl border border-white/10 bg-[#0A0A0A] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="vehicle-banner-selected">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-lg bg-[#E50914] flex items-center justify-center font-black text-white text-xl shrink-0">
          {category}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-mono mb-0.5">Véhicule sélectionné</div>
          <div className="font-bold text-white">{categoryData.name}</div>
          <div className="text-xs text-white/50 italic">Ex : {categoryData.examples}</div>
        </div>
      </div>
      <button
        onClick={openSelector}
        data-testid="services-change-vehicle-btn"
        className="inline-flex items-center gap-2 bg-transparent border border-white/20 hover:border-[#E50914] hover:text-[#E50914] text-white font-semibold text-sm uppercase tracking-wider px-5 py-2.5 rounded transition-all"
      >
        Changer
      </button>
    </div>
  );
}

function ServiceCard({ service, category, categoryData, openSelector }) {
  const { name, tagline, features, premium, highlight, id } = service;
  const { pricing } = usePricing();
  const price = category
    ? (pricing.auto[category]?.[id] ?? categoryData.prices[id])
    : (pricing.auto.A?.[id] ?? CATEGORIES.A.prices[id]);

  const priceBlock = (accent) => (
    <div className="mb-8">
      <div className="text-xs uppercase tracking-wider text-white/40 mb-1">
        {category ? `Cat. ${category} — ${categoryData.name}` : "À partir de"}
      </div>
      <div className="flex items-baseline gap-1">
        {price > 0 ? (
          <>
            <span className="text-6xl font-black text-white tracking-tighter">{price}</span>
            <span className={`text-2xl font-bold ${accent === 'violet' ? 'text-[#F87171]' : 'text-[#E50914]'}`}>€</span>
          </>
        ) : (
          <span className="text-4xl font-black text-white tracking-tighter">Sur devis</span>
        )}
      </div>
      {!category && (
        <button
          onClick={openSelector}
          data-testid={`price-unlock-${id}`}
          className={`mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider hover:gap-2 transition-all ${accent === 'violet' ? 'text-[#F87171] hover:text-[#FCA5A5]' : 'text-[#E50914] hover:text-[#F87171]'}`}
        >
          <Car className="w-3.5 h-3.5" /> Voir mon tarif exact
        </button>
      )}
    </div>
  );

  const handleReserve = (e) => {
    if (!category) {
      e.preventDefault();
      openSelector();
    }
  };

  if (premium) {
    return (
      <div
        data-testid={`service-card-${id}`}
        className="group relative rounded-xl overflow-hidden border border-[#E50914]/50 hover:border-[#F87171] transition-all lg:-translate-y-4 hover:-translate-y-6"
        style={{ boxShadow: '0 0 40px rgba(139, 92, 246, 0.15)' }}
      >
        <div className="absolute inset-0 opacity-40">
          <div className="w-full h-full bg-gradient-to-br from-[#E50914]/30 via-transparent to-transparent" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0015]/70 via-[#0a0015]/85 to-[#0a0015]/95"></div>
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#E50914]/40 rounded-full blur-3xl animate-pulse"></div>

        <div className="relative p-8 flex flex-col h-full">
          <div className="flex items-center gap-2 mb-6">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#E50914]/20 border border-[#E50914]/50 rounded-full">
              <Crown className="w-3 h-3 text-[#F87171]" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#F87171]">Premium</span>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-white mb-1">{name}</h3>
          <div className="text-sm text-[#F87171] mb-8">{tagline}</div>

          {priceBlock('violet')}

          <ul className="space-y-3 mb-8 flex-1">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-white/80">
                <Sparkles className="w-4 h-4 text-[#F87171] shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <a
            href="#reserver"
            onClick={handleReserve}
            data-testid={`book-${id}-button`}
            className="inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white font-bold uppercase text-sm tracking-wider px-6 py-3.5 rounded-lg border border-[#E50914] hover:shadow-[0_0_24px_rgba(229,9,20,0.5)] transition-all"
          >
            {category ? 'Choisir Concession' : 'Choisir mon véhicule'}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`service-card-${id}`}
      className={`group relative rounded-xl overflow-hidden bg-[#0A0A0A] border transition-all duration-300 hover:-translate-y-1
        ${highlight ? 'border-[#E50914]/40 hover:border-[#E50914]' : 'border-white/10 hover:border-white/25'}`}
    >
      {highlight && (
        <div className="absolute top-0 left-0 right-0 bg-[#E50914] text-white text-[10px] uppercase tracking-[0.25em] font-bold text-center py-1.5">
          Le plus demandé
        </div>
      )}

      <div className={`p-8 flex flex-col h-full ${highlight ? 'pt-12' : ''}`}>
        <h3 className="text-2xl font-bold text-white mb-1">{name}</h3>
        <div className="text-sm text-white/50 mb-8">{tagline}</div>

        {priceBlock('red')}

        <ul className="space-y-3 mb-8 flex-1">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm text-white/75">
              <Check className="w-4 h-4 text-[#E50914] shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <a
          href="#reserver"
          onClick={handleReserve}
          data-testid={`book-${id}-button`}
          className={`inline-flex items-center justify-center gap-2 font-bold uppercase text-sm tracking-wider px-6 py-3.5 rounded transition-all
            ${highlight
              ? 'bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white border border-[#E50914] hover:shadow-[0_0_24px_rgba(229,9,20,0.5)]'
              : 'bg-transparent border border-white/20 text-white hover:bg-white hover:text-black'
            }`}
        >
          {category ? 'Réserver' : 'Choisir mon véhicule'}
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}
